import { UtilsService } from "@/services/utils/utils.service";
import { unauthorizedAPI, authorizedAPI } from "@/config/axios.config";
import type { User } from "@/types/user.types";
import type { OnboardingStatus } from "@/types/user.types";

const utils = new UtilsService();

export interface LoginDto {
  email: string;
  password: string;
}

export interface RegisterDto {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  invitationToken?: string;
}

export interface RegisterResponse {
  success?: boolean;
  error?: string;
  message?: string;
  userId?: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface ChangePasswordDto {
  userId: string;
  currentPassword: string;
  newPassword: string;
}

export interface UpdateProfileDto {
  userId: string;
  firstName: string;
  lastName: string;
  phone?: string;
  photo?: string | null;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

class AuthService {
  register(userData: RegisterDto): Promise<RegisterResponse> {
    return utils.handleApiRequest<RegisterResponse>(() =>
      unauthorizedAPI.post("/auth/register", userData)
    );
  }

  login(userData: LoginDto): Promise<LoginResponse> {
    return utils.handleApiRequest<LoginResponse>(() =>
      unauthorizedAPI.post("/auth/login", userData)
    );
  }

  logout() {
    return utils.handleApiRequest(() => authorizedAPI.post("/auth/logout"));
  }

  getProfile(): Promise<User> {
    return utils.handleApiRequest<User>(async () =>
      authorizedAPI.get("/auth/profile")
    );
  }

  sendOtp(email: string): Promise<{ success?: boolean; message?: string }> {
    return utils.handleApiRequest<{ success?: boolean; message?: string }>(() =>
      unauthorizedAPI.post("/auth/request-password-reset", { email })
    );
  }

  verifyEmail({ email, OTP }: { email: string; OTP: string }): Promise<{ success?: boolean; message?: string }> {
    // Backend expects 'otp' key (lowercase)
    return utils.handleApiRequest<{ success?: boolean; message?: string }>(() =>
      unauthorizedAPI.post("/auth/validate/email", { email, otp: OTP })
    );
  }

  resetPassword({
    email,
    OTP,
    newPassword,
  }: {
    email: string;
    OTP: string;
    newPassword: string;
  }): Promise<{ success?: boolean; message?: string }> {
    return utils.handleApiRequest<{ success?: boolean; message?: string }>(() =>
      unauthorizedAPI.post("/auth/reset-password", { email, otp: OTP, newPassword })
    );
  }

  changePassword({ userId, currentPassword, newPassword }: ChangePasswordDto): Promise<{ success?: boolean; message?: string }> {
    return utils.handleApiRequest<{ success?: boolean; message?: string }>(() =>
      authorizedAPI.post("/auth/change-password", {
        currentPassword,
        newPassword,
      })
    );
  }

  updateProfile({ userId, firstName, lastName, phone, photo }: UpdateProfileDto) {
    return utils.handleApiRequest(() =>
      authorizedAPI.put("/auth/profile", { userId, firstName, lastName, phone, photo })
    );
  }

  uploadAvatar(file: File): Promise<{ success?: boolean; message?: string }> {
    const formData = new FormData();
    formData.append("file", file);

    return utils.handleApiRequest<{ success?: boolean; message?: string }>(() =>
      authorizedAPI.post("/auth/upload-avatar", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
    );
  }

  // OTP endpoints
  generateOtp(email: string, type: string): Promise<{ success?: boolean; message?: string }> {
    return utils.handleApiRequest<{ success?: boolean; message?: string }>(() =>
      unauthorizedAPI.post("/otp/generate", { email, type })
    );
  }

  validateOtp(email: string, otp: string, type: string): Promise<{ success?: boolean; message?: string }> {
    return utils.handleApiRequest<{ success?: boolean; message?: string }>(() =>
      unauthorizedAPI.post("/otp/validate", { email, otp, type })
    );
  }

  // Google OAuth endpoints
  initiateGoogleLogin() {
    return utils.handleApiRequest(() =>
      unauthorizedAPI.get("/auth/google")
    );
  }

  handleGoogleCallback() {
    return utils.handleApiRequest(() =>
      unauthorizedAPI.get("/auth/google/callback")
    );
  }

  // Token refresh
  refreshToken() {
    return utils.handleApiRequest(() =>
      unauthorizedAPI.post("/auth/refresh-token")
    );
  }

  // Onboarding status
  getOnboardingStatus(): Promise<OnboardingStatus> {
    return utils.handleApiRequest<OnboardingStatus>(() =>
      authorizedAPI.get("/auth/onboarding-status")
    );
  }
}

export const authService = new AuthService();
